import { createStore } from 'vuex'
import { apiFetch } from '@/api/apiFetch'

export default createStore({
  state: {
    user: null, // Holds all user related data
    platformExchanges: null, // Holds all exchanges we support
    strategies: null, // Holds all strategies from API
    exchangeTradingPairs: {}, // Holds all trading pairs per exchangeId
    getUserLogsPage: 1, // Init
    getUserLogsLimit: 10, // Init
    theme: 'light', // Init
    protectPrivacy: false // Init
  },
  getters: {
    isUserLoggedIn (state) {
      return (state.user?.profile?.session_token)? true : false
    },
    getUser (state) {
      return state.user
    },
    getUserProfile (state) {
      return state.user?.profile
    },
    getUserStripePortalSession (state) {
      return state.user?.stripeSession
    },
    getUserBots (state) {
      return state.user?.bots
    },
    getUserBotAssets (state) {
      return state.user?.botAssets
    },
    getUserBotErrors (state) {
      return state.user?.botErrors
    },
    getUserDashboardUnifiedAssetData (state) {
      return state.user?.dashboard?.unifiedAssetData?.unifiedData // Returns null if it does not exist, which is important for logic to trigger fetching the missing data
    },
    getUserLogs (state) {
      return state.user?.logs
    },
    getUserLogsPageLimit(state) {
      return { page: state.getUserLogsPage, limit: state.getUserLogsLimit }
    },
    getUserBotLogs: (state) => (botId) => {
      if (state.user?.botLogs && state.user.botLogs[botId]) {
        return state.user.botLogs[botId]
      }
      return []
    },
    getPlatformExchanges (state) {
      return state.platformExchanges
    },
    getUTMs (state) {
      return state.user?.utms
    },
    getTheme (state) {
      return state.theme
    },
    getProtectPrivacy (state) {
      return state.protectPrivacy
    },
    getStrategies (state) {
      return state.strategies
    },
    getStrategyByName: (state) => (name) => {
      if (!state.strategies) return null
      return state.strategies[name] || null
    }
  },
  actions: {
    getPlatformExchanges: async ({ commit, getters }) => {
      console.log('getPlatformExchanges')

      if (!getters.isUserLoggedIn) return null
      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchanges'
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects
        commit('setPlatformExchanges', { exchanges: response })
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }
    },
    getExchangeTradingPairs: async ({ commit, getters }, { exchangeId, tradingAccount, botId }) => {
      console.log('getExchangeTradingPairs')

      if (!exchangeId || !botId) return null

      if (!getters.isUserLoggedIn) return null
      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/pairs/'+exchangeId+'?botId='+botId
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Remove all spaces and slashes
        const tradingPairs = response.map(pair => {
          pair.symbol = pair.symbol.replace('/', '').replace(' ', '')
          return pair
        });

        commit('setExchangeTradingPairs', { exchangeId: exchangeId, tradingAccount: tradingAccount, exchangeTradingPairs: tradingPairs })

        // Unset all bot errors
        // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar
        // commit('setUserBotError', { botId: botId, errorResponseObj: null})
      } else {
        const response = await rawResponse.json()

        // Store the error this bot produced so we can show it to the user when necessary
        commit('setUserBotError', { botId: botId, errorResponseObj: response})
      }
    },
    getExchangeTradingPairsByApiKeys: async ({ commit, getters }, { exchangeId, tradingAccount, apiKey, apiSecret, apiPassphrase }) => {
      console.log('getExchangeTradingPairs')

      // Do not validate the apiPassphrase because not all exchanges need it
      if (!exchangeId || !tradingAccount || !apiKey || !apiSecret) return null

      if (!getters.isUserLoggedIn) return null
      let postData = {
        apiKey: apiKey,
        secretKey: apiSecret.replace(/\\n/g, "\n"), // Fixing the JSON.stringify issue for Coinbase
        passphrase: apiPassphrase,
        publicAddress: apiPassphrase,
        tradingAccount: tradingAccount,
        isTestNet: false
      }

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/pairs/'+exchangeId
      const rawResponse = await apiFetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Remove all spaces and slashes
        const tradingPairs = response.map(pair => {
          pair.symbol = pair.symbol.replace('/', '')
          pair.symbol = pair.symbol.replace(' ', '')
          return pair
        });

        commit('setExchangeTradingPairs', { exchangeId: exchangeId, tradingAccount: tradingAccount, exchangeTradingPairs: tradingPairs })
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }
    },
    getBotAssets: async ({ commit, getters }, { exchangeId, botId }) => {
      // Check if enough time passed to justify fetching it again
      const botAssets = getters.getUserBotAssets
      if (botAssets && botAssets[botId]) {

        const updatedAt = botAssets[botId].updatedAt
        const timeNow = Math.floor(new Date().getTime() / 1000)

        // No need to update botAssets again within 10 seconds
        if (timeNow -10 <= updatedAt) return true
      }

      console.log('getBotAssets', botId)

      if (!exchangeId || !botId) return null

      if (!getters.isUserLoggedIn) return null
      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/assets/'+exchangeId+'?botId='+botId
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Store assets
        commit('setUserBotAssets', { botId: botId, assets: response })

        // Unset all bot errors
        // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar
        // commit('setUserBotError', { botId: botId, errorResponseObj: null})
      } else {
        // Store assets
        commit('setUserBotAssets', { botId: botId, assets: {} })

        const response = await rawResponse.json()
        // Store the error this bot produced so we can show it to the user when necessary
        commit('setUserBotError', { botId: botId, errorResponseObj: response})
      }
    },
    getBotAssetsByApiKeys: async ({ commit, getters }, { exchangeId, exchangeTradingAccount, apiKey, apiSecret, apiPassphrase }) => {
      console.log('getBotAssetsByApiKeys')

      // Do not validate the apiPassphrase because not all exchanges need it
      if (!exchangeId || !exchangeTradingAccount || !apiKey || !apiSecret) return null

      if (!getters.isUserLoggedIn) return null
      let postData = {
        apiKey: apiKey,
        secretKey: apiSecret.replace(/\\n/g, "\n"), // Fixing the JSON.stringify issue for Coinbase
        passphrase: apiPassphrase,
        publicAddress: apiPassphrase,
        tradingAccount: exchangeTradingAccount,
        isTestNet: false
      }

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/exchange/assets/'+exchangeId
      const rawResponse = await apiFetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Store assets - 0 is used as a temporary ID until the Bot get's an ID from the API
        commit('setUserBotAssets', { botId: 0, assets: response })

      } else {
        // Store assets - 0 is used as a temporary ID until the Bot get's an ID from the API
        commit('setUserBotAssets', { botId: 0, assets: {} })
      }
    },
    getUserBots: async ({ commit, getters }) => {
      console.log('getUserBots')

      if (!getters.isUserLoggedIn) return null
      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots'
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects
        commit('setUserBots', { userBots: response })
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }
    },
    getUserLogs: async ({ commit, getters }, {page, limit}) => {
      console.log('getUserLogs')

      if (!getters.isUserLoggedIn) return null
      // Fetch page and limit from the store
      const userLogsPageLimit = getters.getUserLogsPageLimit
      if (!page) page = userLogsPageLimit.page
      if (!limit) limit = userLogsPageLimit.limit

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/auditlog?page='+page+'&limit='+limit
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()
        commit('setUserLogs', { logs: response })
      } else {
        // TODO: Handle errors
      }
    },
    getUserBotLogs: async ({ commit, getters }, { botId, page, limit }) => {
      console.log('getUserBotLogs')

      if (!getters.isUserLoggedIn) return null
      if (!page) page = 1
      if (!limit) limit = 100

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/auditlog/bot/'+botId+'?page='+page+'&limit='+limit
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()
        commit('setUserBotLogs', { botId: botId, logs: response })

        // Unset all bot errors
        // TODO: Cannot do it here because it would remove other API call errors if this API call works. We must show all errors until the user reloads the bot page or similar
        // commit('setUserBotError', { botId: botId, errorResponseObj: null})
      } else {
        const response = await rawResponse.json()

        // Store the error this bot produced so we can show it to the user when necessary
        commit('setUserBotError', { botId: botId, errorResponseObj: response})
      }
    },
    createBot: async ({ dispatch, commit, getters }, bot) => {
      console.log('createBot', bot)

      if (!bot) return null

      if (!getters.isUserLoggedIn) return null
      const postData = bot

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots'
      const rawResponse = await apiFetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Invalidate Dashboard cache so the new bot appears on the next Dashboard visit.
        // See deleteBot for the full rationale (cache-guard semantics, no eager refetch).
        commit('setUserDashboardUnifiedAssetData', { unifiedData: null, assetPrices: {} })

        // TODO: it's better to fetch only that 1 bot and add it to the state manually
        // now fetch all user bots fresh
        await dispatch('getUserBots')

        return response

      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }

      return {}
    },
    updateBot: async ({ dispatch, commit, getters }, bot) => {
      // console.log('updateBot', bot)

      if (!getters.isUserLoggedIn) return null
      const postData = bot

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots/' + bot.id
      const rawResponse = await apiFetch(url, {
        method: "PATCH",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        // const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        // Unset all bot errors
        // TODO: We cannot do this here. It can only be reset when we allow updating the API Keys!
        // commit('setUserBotError', { botId: bot.id, errorResponseObj: null})

        // Invalidate Dashboard cache — edits to title/category/exchangeId/enabled are read by the Dashboard.
        // See deleteBot for the full rationale (cache-guard semantics, no eager refetch).
        commit('setUserDashboardUnifiedAssetData', { unifiedData: null, assetPrices: {} })

        // TODO: It's better to fetch only that 1 bot and add it to the state manually
        // Fetch all user bots again
        await dispatch('getUserBots')

      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }
    },
    deleteBot: async ({ dispatch, commit, getters }, bot) => {
      // console.log('deleteBot', bot)

      if (!getters.isUserLoggedIn) return null
      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/bots/' + bot.id
      const rawResponse = await apiFetch(url, {
        method: "DELETE",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        // Unset all bot errors
        commit('setUserBotError', { botId: bot.id, errorResponseObj: null})

        // Invalidate the cached Dashboard data so the next visit refetches.
        // Without this, PageDashboard.vue's `created()` cache guard serves stale
        // data that still references the deleted bot, breaking getBotById() lookups.
        // We don't refetch eagerly here — fetchAllUserDashboardAssets is API-heavy.
        commit('setUserDashboardUnifiedAssetData', { unifiedData: null, assetPrices: {} })

        // TODO: it's better to fetch only that 1 bot and add it to the state manually
        // now fetch all user bots fresh
        await dispatch('getUserBots')

        // TODO: Loading page = 1 and limit = 10 here is basically overruling whatever the user had loaded already in the LogsList component. Those value are not in sync anymore. Not the best.
        // TODO: Maybe it's better to have getUserLogsPage and getUserLogsLimit in the store and use those accross the whole app.
        const userLogsPageLimit = getters.getUserLogsPageLimit
        await dispatch('getUserLogs', {page: userLogsPageLimit.page, limit: userLogsPageLimit.limit})

      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }
    },
    deleteUserAccount: async () => {
      const url = process.env.VUE_APP_API_URL + '/users/me'
      
      const rawResponse = await apiFetch(url, {
        method: "DELETE",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      })
      
      return await rawResponse.json()
    },
    fetchAllUserDashboardAssets: async ({ dispatch, commit, getters }) => {
      console.log('fetchAllUserDashboardAssets')

      //*** Define vars and do initial checks ***//
      if (!getters.getUserBots) await dispatch('getUserBots')
      if (!getters.isUserLoggedIn) return null

      const bots = getters.getUserBots

      if (!bots || bots.length <= 0) {
        commit('setUserDashboardUnifiedAssetData', { unifiedData: [], assetPrices: {} })
        return false
      }

      //*** Fetch all balances and positions from all bots in parallel batches ***//
      const batchSize = 10
      const allFetchedData = []

      for (let i = 0; i < bots.length; i += batchSize) {
        const batch = bots.slice(i, i + batchSize)

        const batchPromises = batch.map(async (bot) => {
          const url = `${process.env.VUE_APP_API_URL}/exchange/assets/${bot.exchangeId}?botId=${bot.id}`

          try {
            const rawResponse = await apiFetch(url, {
              method: "GET",
              mode: "cors",
              cache: "no-cache",
              headers: {
                'Accept': 'application/json'
              }
            })

            commit('setDashboardNowFetchingBot', { bot })

            if (rawResponse.status === 200) {
              const response = await rawResponse.json()
              return { bot, response, success: true }
            } else {
              const errorResponse = await rawResponse.json()
              return { bot, response: null, success: false, error: errorResponse }
            }
          } catch (error) {
            console.error(`Error fetching data for bot ${bot.id}:`, error)
            return { bot, response: null, success: false, error }
          }
        })

        const batchResults = await Promise.all(batchPromises)
        allFetchedData.push(...batchResults)
      }

      //*** Build unified data array ***//
      const unifiedData = []

      for (const result of allFetchedData) {
        const { bot, success, response, error } = result
        const botCategory = (!bot.category || bot.category === '') ? null : bot.category
        const dedupeKey = `${bot.exchangeConfig.apiKey}_${bot.exchangeConfig?.publicAddress}`

        if (success && response) {
          // Keep existing per-bot commit for other components
          commit('setUserBotAssets', { botId: bot.id, assets: response })

          // Process assets (SPOT)
          for (const asset of response.balance) {
            unifiedData.push({
              type: 'asset',
              botId: bot.id,
              exchangeId: bot.exchangeId,
              botCategory: botCategory,
              dedupeKey: `asset_${dedupeKey}_${asset.coin}`,
              coin: asset.coin,
              balance: Number(asset.balance),
              // USD fields will be added after price fetch
              balanceUSD: null
            })
          }

          // Process positions (FUTURES)
          for (const position of response.positions) {
            unifiedData.push({
              type: 'position',
              botId: bot.id,
              exchangeId: bot.exchangeId,
              botCategory: botCategory,
              dedupeKey: `position_${dedupeKey}_${position.baseCoin}_${position.quoteCoin}`,
              coin: position.quoteCoin,
              balance: Number(position.currentCollateralAsQuoteCoin),
              // Keep all raw position data
              ...position, // We need more of its fields later on
              // USD fields will be added after price fetch
              balanceUSD: null,
              unrealizedPnlAsQuoteCoinUSD: null,
              currentCollateralAsQuoteCoinUSD: null
            })
          }

          // Clear bot error on success
          // commit('setUserBotError', { botId: bot.id, errorResponseObj: null })
        } else {
          commit('setUserBotAssets', { botId: bot.id, assets: {} })
          commit('setUserBotError', { botId: bot.id, errorResponseObj: error })
        }
      }

      //*** Collect unique coins for price fetching ***//
      const fiatCurrencies = new Set(["AFN","EUR","ALL","DZD","USD","AOA","XCD","ARS","AMD","AWG","AUD","AZN","BSD","BHD","BDT","BBD","BYN","BZD","XOF","BMD","INR","BTN","BOV","BAM","BWP","NOK","BRL","BND","BGN","BIF","CVE","KHR","XAF","CAD","KYD","CLP","CLF","CNY","COP","COU","KMF","CDF","NZD","CRC","CUP","ANG","CZK","DKK","DJF","DOP","EGP","SVC","ERN","SZL","ETB","FKP","FJD","XPF","GMD","GEL","GHS","GIP","GTQ","GBP","GNF","GYD","HTG","HNL","HKD","HUF","ISK","IDR","XDR","IRR","IQD","ILS","JMD","JPY","JOD","KZT","KES","KPW","KRW","KWD","KGS","LAK","LBP","LSL","ZAR","LRD","LYD","CHF","MOP","MKD","MGA","MWK","MYR","MVR","MRU","MUR","XUA","MXN","MXV","MDL","MAD","MZN","MMK","NAD","NPR","NIO","NGN","OMR","PKR","PAB","PGK","PYG","PEN","PHP","PLN","QAR","RON","RUB","RWF","SHP","WST","STN","SAR","RSD","SCR","SLE","SGD","XSU","SBD","SOS","SSP","LKR","SDG","SRD","SEK","CHE","CHW","SYP","TWD","TJS","TZS","THB","TOP","TTD","TND","TRY","TMT","UGX","UAH","AED","USN","UYU","UYI","UYW","UZS","VUV","VES","VED","VND","YER","ZMW","ZWG"])
      const ignoredCoins = new Set(['CRO.STAKED'])

      const userUniqueFiats = []
      const userUniqueCryptos = []

      for (const entry of unifiedData) {
        const coin = entry.coin
        if (ignoredCoins.has(coin)) continue

        if (fiatCurrencies.has(coin)) {
          if (!userUniqueFiats.includes(coin)) userUniqueFiats.push(coin)
        } else {
          if (!userUniqueCryptos.includes(coin)) userUniqueCryptos.push(coin)
        }

        // Also include quoteCoin from positions for USD conversion
        if (entry.type === 'position' && entry.quoteCoin) {
          if (!userUniqueCryptos.includes(entry.quoteCoin)) {
            userUniqueCryptos.push(entry.quoteCoin)
          }
        }
      }

      if (userUniqueFiats.length === 0 && userUniqueCryptos.length === 0) {
        console.log('No assets to load prices for.')
        commit('setUserDashboardUnifiedAssetData', { unifiedData: [], assetPrices: {} })
        return false
      }

      //*** Fetch USD prices ***//
      let fiatUsdPrices = {}
      let cryptoUsdPrices = {}

      if (userUniqueFiats.length > 0) {
        const fiatApiUrl = `https://min-api.cryptocompare.com/data/pricemulti?fsyms=${userUniqueFiats.join(',')}&tsyms=USD`
        try {
          const fiatRawResponse = await fetch(fiatApiUrl, { method: "GET", mode: "cors", cache: "no-cache" })
          if (fiatRawResponse.status === 200) {
            fiatUsdPrices = await fiatRawResponse.json()
          }
        } catch (e) {
          console.log("Could not fetch fiat USD prices from the prices API.")
        }
      }

      if (userUniqueCryptos.length > 0) {
        const cryptoApiUrl = `${process.env.VUE_APP_API_URL}/prices?fsyms=${userUniqueCryptos.join(',')}&tsyms=USD`
        try {
          const cryptoRawResponse = await apiFetch(cryptoApiUrl, {
            method: "GET",
            mode: "cors",
            cache: "no-cache",
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          })
          if (cryptoRawResponse.status === 200) {
            cryptoUsdPrices = await cryptoRawResponse.json()
          }
        } catch (e) {
          console.log("Could not fetch crypto USD prices from the prices API.")
        }
      }

      const assetPrices = { ...fiatUsdPrices, ...cryptoUsdPrices }

      if (Object.keys(assetPrices).length === 0) {
        console.log("Prices API did not return prices for given user assets.")
        // Transient failure (prices API down or returned empty) — commit `null`
        // instead of `[]` so the next Dashboard mount retries automatically.
        // `[]` would be cached as "legitimately empty" and lock the user into
        // a "No assets found" state until they manually click Refresh Assets.
        commit('setUserDashboardUnifiedAssetData', { unifiedData: null, assetPrices: {} })
        return false
      }

      //*** Enrich unified data with USD values ***//
      for (const entry of unifiedData) {
        if (entry.type === 'asset') {
          if (assetPrices[entry.coin]) {
            entry.balanceUSD = Number(assetPrices[entry.coin].USD) * entry.balance
          } else {
            console.log(`Asset: Did not find USD price for ${entry.coin}`)
          }
        } else if (entry.type === 'position') {
          if (assetPrices[entry.quoteCoin]) {
            const priceUSD = Number(assetPrices[entry.quoteCoin].USD)
            entry.unrealizedPnlAsQuoteCoinUSD = priceUSD * Number(entry.unrealizedPnlAsQuoteCoin)
            entry.currentCollateralAsQuoteCoinUSD = priceUSD * Number(entry.currentCollateralAsQuoteCoin)
          } else {
            console.log(`Position: Did not find USD price for ${entry.quoteCoin}`)
          }

          // For position balance USD (used in exchange/category totals)
          if (assetPrices[entry.coin]) {
            entry.balanceUSD = Number(assetPrices[entry.coin].USD) * entry.balance
          }
        }
      }

      //*** Commit unified data to store ***//
      commit('setUserDashboardUnifiedAssetData', { unifiedData, assetPrices })
    },
    getUserProfile: async ({ commit, getters }) => {
      console.log('fetchUserProfile')

      if (!getters.isUserLoggedIn) return null
      // Make API call
      const url = process.env.VUE_APP_API_URL+'/users/me'
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()
        commit('setUserProfile', { userProfile: response })
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
      }
    },
    getUserStripePortalSession: async ({ commit, getters }) => {
      console.log('getUserStripePortalSession')

      if (!getters.isUserLoggedIn) return null
      // Don't fetch it again if it's still valid
      const timeSecondsNow = Math.floor(Date.now() / 1000)
      const stripeSession = getters.getUserStripePortalSession
      if (stripeSession && stripeSession.created + 60*10 >= timeSecondsNow) {
        return stripeSession
      }

      // Make API call
      const url = process.env.VUE_APP_API_URL+'/users/me/stripe/portal_session'
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()

        commit('setUserStripePortalSession', { userStripeSession: response })
        return response

      } else {
        // TODO: Handle errors
      }

      return null
    },
    getUserCannyToken: async ({ commit, getters }) => {
      console.log('getUserCannyToken')

      if (!getters.isUserLoggedIn) return null
      // Make API call
      const url = process.env.VUE_APP_API_URL+'/users/me/canny_io/token'
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Accept': 'application/json'
        }
      })

      // Check status codes, handle errors and process response
      if (rawResponse.status === 200) {
        const response = await rawResponse.json()

        commit('setUserCannyToken', { cannyTokenResponse: response })
        return response

      } else {
        // TODO: Handle errors
      }

      return null
    },
    getStrategies: async ({ commit, getters }) => {
      if (!getters.isUserLoggedIn) return null
      // Don't fetch again if already loaded
      if (getters.getStrategies) return getters.getStrategies

      const url = process.env.VUE_APP_API_URL+'/strategies'
      const rawResponse = await apiFetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        headers: {
          'Accept': 'application/json'
        }
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json()
        commit('setStrategies', { strategies: response })
        return response
      }

      return null
    },
    addUserToStrategy: async ({ getters }, codes) => {
      if (!getters.isUserLoggedIn) return null
      let postData = {
        strategyCodes: codes
      }

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/strategies/add'
      const rawResponse = await apiFetch(url, {
        method: "PATCH",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        return true

      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }

      return false
    },
    updateUserProfile: async ({ commit, getters }, newUserProfile) => {
      console.log('updateUserProfile')

      if (!getters.isUserLoggedIn) return null
      const postData = newUserProfile

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/users/me'
      const rawResponse = await apiFetch(url, {
        method: "PATCH",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        const response = await rawResponse.json() // parses JSON response into native JavaScript objects

        await commit('setUserProfile', { userProfile: response})

        return true
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }

      return false
    },
    sendTradingSignal: async ({ getters }, { signal }) => {
      // console.log('sendTradingSignal')

      if (!getters.isUserLoggedIn) return null
      const postData = signal

      // make an api call to our API to get the user token
      const url = process.env.VUE_APP_API_URL+'/signals'
      const rawResponse = await apiFetch(url, {
        method: "POST",
        mode: "cors", // no-cors, *cors, same-origin
        cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(postData), // body data type must match "Content-Type" header
      })

      if (rawResponse.status === 200) {
        // const response = await rawResponse.json() // parses JSON response into native JavaScript objects
        return true
      } else {
        // 401 is handled centrally by apiFetch (logout + redirect).
        // TODO: handle other non-200 states (server errors, validation failures, etc.)
      }

      return false
    },
    logout: ({ commit }) => {
      commit('resetUser')
    }
  },
  mutations: {
    setPlatformExchanges (state, { exchanges }) {
      state.platformExchanges = exchanges
    },
    setExchangeTradingPairs (state, { exchangeId, tradingAccount, exchangeTradingPairs }) {
      if (!state.exchangeTradingPairs[exchangeId]) state.exchangeTradingPairs[exchangeId] = {}
      if (!state.exchangeTradingPairs[exchangeId][tradingAccount]) state.exchangeTradingPairs[exchangeId][tradingAccount] = []

      state.exchangeTradingPairs[exchangeId][tradingAccount] = exchangeTradingPairs
    },
    setUserProfile (state, { userProfile }) {
      if (!state.user) state.user = {}

      // /users/me responses don't include the session_token (only /authenticate
      // does). Preserve the existing token across profile refreshes so users
      // stay logged in through every subsequent call.
      if (!userProfile.session_token) {
        userProfile.session_token = state.user.profile?.session_token
      }

      state.user.profile = userProfile

      // Persist ONLY the session_token (raw string) under a clearly-named key.
      // Everything else (id, email, accessTokenCurrent, subscription info, …)
      // is PII / a long-lived legacy token that doesn't need to live at rest
      // in the browser — it gets re-fetched from /users/me on every page load.
      // Keeps localStorage minimal and reduces the blast radius of a
      // localStorage leak (XSS, shared computer, dev-tools snooping).
      if (userProfile.session_token) {
        localStorage.setItem('sessionToken', userProfile.session_token)
      } else {
        localStorage.removeItem('sessionToken')
      }
    },
    setUserStripePortalSession (state, { userStripeSession }) {
      if (!state.user) state.user = {}

      state.user.stripeSession = userStripeSession
    },
    setUserCannyToken (state, { cannyTokenResponse }) {
      if (!state.user) state.user = {}

      state.user.canny = cannyTokenResponse
    },
    resetUser (state) {
      state.user = null
      localStorage.removeItem('sessionToken')
      localStorage.removeItem('userProfile') // legacy key — clean up if any user still has it
      localStorage.removeItem('userUtms')
    },
    setUserBots (state, { userBots }) {
      state.user.bots = userBots
    },
    setUserBotAssets (state, { botId, assets }) {
      if (!state.user) state.user = {}
      if (!state.user.botAssets) state.user.botAssets = {}

      if (assets) {
        const updatedAt = Math.floor(new Date().getTime() / 1000)
        assets.updatedAt = updatedAt // Add the last updated date so we don't fetcht his too often from API
        state.user.botAssets[botId] = assets // Set it
      } else {
        delete state.user.botAssets[botId] // Unset it if it's null
      }
    },
    setUserBotError (state, { botId, errorResponseObj }) {
      if (!state.user) state.user = {}
      if (!state.user.botErrors) state.user.botErrors = {}

      if (errorResponseObj) state.user.botErrors[botId] = errorResponseObj // Set it
      else delete state.user.botErrors[botId] // Unset it if it's null
    },
    setDashboardNowFetchingBot (state, { bot }) {
      if (!state.user) state.user = {}
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.nowFetchingBot = bot
    },
    setUserDashboardUnifiedAssetData (state, { unifiedData, assetPrices }) {
      if (!state.user.dashboard) state.user.dashboard = {}
      state.user.dashboard.unifiedAssetData = {
        unifiedData,
        assetPrices
      }
    },
    setUserBotLogs (state, { botId, logs }) {
      if (!state.user.botLogs) {
        state.user.botLogs = {}
      }
      state.user.botLogs[botId] = logs
    },
    setUserLogs (state, { logs }) {
      if (!state.user.logs) {
        state.user.logs = {}
      }
      state.user.logs = logs
    },
    setUserLogsPageLimit (state, { page, limit }) {
      state.getUserLogsPage = page
      state.getUserLogsLimit = limit
    },
    setUTMs (state, { utms }) {
      if (!state.user) state.user = {}

      // Set them fresh if they are not empty
      if (utms.medium || utms.source || utms.campaign || utms.term || utms.content) {
        state.user.utms = utms
        localStorage.setItem('userUtms', JSON.stringify(utms))
      } else { // Otherwise load them from local storage to always have them available
        utms = JSON.parse(localStorage.getItem("userUtms"));
        state.user.utms = utms
      }
    },
    setStrategies (state, { strategies }) {
      state.strategies = strategies
    },
    setTheme (state, { theme }) {
      // Set fresh if it's not empty
      if (theme) {
        state.theme = theme
        localStorage.setItem('theme', theme)
      } else { // Otherwise fetch from local storage
        theme = localStorage.getItem('theme');

        // If theme was never set, set the default theme
        if (!theme || theme == '') {
          theme = 'light'
          localStorage.setItem('theme', theme)
        }

        // Store it in local
        state.theme = theme
      }
    },
    setProtectPrivacy (state, { privacy }) {
      // Set fresh if it's not empty
      if (privacy === true || privacy === false) {
        state.protectPrivacy = privacy
        localStorage.setItem('protectPrivacy', privacy)
      } else { // Otherwise fetch from local storage
        privacy = localStorage.getItem('protectPrivacy') == 'true' ? true : false

        // If theme was never set, set the default theme
        if (!privacy || privacy == '') {
          privacy = false
          localStorage.setItem('protectPrivacy', privacy)
        }

        // Store it in local
        state.protectPrivacy = privacy
      }
    }
  }
})
